/**
 * DesktopMenuA11y - Disclosure navigation keyboard support for desktop menu.
 *
 * Implements the Disclosure Navigation pattern per:
 * https://www.w3.org/WAI/ARIA/apg/patterns/disclosure/examples/disclosure-navigation/
 *
 * Tab moves through all items naturally. Arrow keys provide enhanced navigation.
 * Layers on top of the existing hover-based menu in Header.js.
 */

function DesktopMenuA11y({ node }) {
	if (!node) return;

	const mediaQuery = window.matchMedia('(min-width: 960px)');
	if (!mediaQuery.matches) return;

	const menuList = node.querySelector('ul[aria-label="Main menu"]');
	if (!menuList) return;

	const triggerLis = Array.from(
		menuList.querySelectorAll(':scope > li[data-menu-trigger]')
	);

	let closeTimeout = null;

	/*
	 * Helpers
	 */

	function getTriggerElement(li) {
		return li.querySelector(':scope > a, :scope > button');
	}

	function getPanel(li) {
		return li.querySelector(':scope > [data-menu-target]');
	}

	function getDirectPanelLinks(panel) {
		if (!panel) return [];
		return Array.from(
			panel.querySelectorAll('a[href]')
		).filter(a => !a.closest('[data-flyout]'));
	}

	function getFlyout(link) {
		const li = link.closest('li');
		if (!li || !li.hasAttribute('data-has-children')) return null;
		return li.querySelector(':scope > [data-flyout]');
	}

	function getFlyoutLinks(flyout) {
		if (!flyout) return [];
		return Array.from(flyout.querySelectorAll('a[href]'));
	}

	function isInsideFlyout(link) {
		return !!link.closest('[data-flyout]');
	}

	function openDropdown(li) {
		triggerLis.forEach(otherLi => {
			const otherPanel = getPanel(otherLi);
			const otherTrigger = getTriggerElement(otherLi);
			if (otherPanel) otherPanel.classList.remove('is-open');
			if (otherTrigger) otherTrigger.setAttribute('aria-expanded', 'false');
		});

		const panel = getPanel(li);
		const trigger = getTriggerElement(li);
		if (panel) panel.classList.add('is-open');
		if (trigger) trigger.setAttribute('aria-expanded', 'true');
	}

	function closeAllDropdowns() {
		triggerLis.forEach(li => {
			const panel = getPanel(li);
			const trigger = getTriggerElement(li);
			if (panel) panel.classList.remove('is-open');
			if (trigger) trigger.setAttribute('aria-expanded', 'false');
		});
		closeAllFlyouts();
	}

	function openFlyout(flyout, triggerLink) {
		if (!flyout) return;
		flyout.classList.add('is-open');
		if (triggerLink) triggerLink.setAttribute('aria-expanded', 'true');
	}

	function closeFlyout(flyout, triggerLink) {
		if (!flyout) return;
		flyout.classList.remove('is-open');
		if (triggerLink) triggerLink.setAttribute('aria-expanded', 'false');
	}

	function closeAllFlyouts() {
		node.querySelectorAll('[data-flyout].is-open').forEach(flyout => {
			flyout.classList.remove('is-open');
			const parentLi = flyout.closest('li[data-has-children]');
			if (parentLi) {
				const parentLink = parentLi.querySelector(':scope > a');
				if (parentLink) parentLink.setAttribute('aria-expanded', 'false');
			}
		});
	}

	function getOpenPanel() {
		for (const li of triggerLis) {
			const panel = getPanel(li);
			if (panel && panel.classList.contains('is-open')) {
				return { li, panel };
			}
		}
		return null;
	}

	function getTriggerIndex(li) {
		return triggerLis.indexOf(li);
	}

	/*
	 * Tab interception for disclosure pattern
	 */

	function handleTab(event) {
		const { shiftKey } = event;
		const activeEl = document.activeElement;

		const open = getOpenPanel();
		if (!open) return;

		const { li: openLi, panel: openPanel } = open;

		// Check if active element is inside a flyout
		if (isInsideFlyout(activeEl)) {
			const flyout = activeEl.closest('[data-flyout]');
			const flyoutItems = getFlyoutLinks(flyout);
			const currentIdx = flyoutItems.indexOf(activeEl);

			if (!shiftKey && currentIdx === flyoutItems.length - 1) {
				// Tab past last flyout item -> close flyout, move to next panel item
				const parentLi = flyout.closest('li[data-has-children]');
				const parentLink = parentLi
					? parentLi.querySelector(':scope > a')
					: null;
				closeFlyout(flyout, parentLink);

				const directLinks = getDirectPanelLinks(openPanel);
				const parentIdx = directLinks.indexOf(parentLink);
				if (parentIdx < directLinks.length - 1) {
					event.preventDefault();
					directLinks[parentIdx + 1].focus();
				} else {
					// Last item in panel -> close and move on
					event.preventDefault();
					closeAllDropdowns();
					const nextTriggerIdx = getTriggerIndex(openLi) + 1;
					if (nextTriggerIdx < triggerLis.length) {
						getTriggerElement(triggerLis[nextTriggerIdx]).focus();
					}
				}
				return;
			}

			if (shiftKey && currentIdx === 0) {
				// Shift+Tab from first flyout item -> close flyout, focus parent trigger
				event.preventDefault();
				const parentLi = flyout.closest('li[data-has-children]');
				const parentLink = parentLi
					? parentLi.querySelector(':scope > a')
					: null;
				closeFlyout(flyout, parentLink);
				if (parentLink) parentLink.focus();
				return;
			}

			// Let browser handle normal Tab within flyout
			return;
		}

		// Check if active element is inside the open panel (but not flyout)
		if (openPanel.contains(activeEl)) {
			const directLinks = getDirectPanelLinks(openPanel);
			const currentIdx = directLinks.indexOf(activeEl);

			if (!shiftKey) {
				// Check if current link has a closed flyout that should be opened
				const flyout = getFlyout(activeEl);
				if (flyout && !flyout.classList.contains('is-open')) {
					event.preventDefault();
					openFlyout(flyout, activeEl);
					const flyoutItems = getFlyoutLinks(flyout);
					if (flyoutItems.length > 0) {
						flyoutItems[0].focus();
					}
					return;
				}

				if (currentIdx === directLinks.length - 1) {
					// Tab past last panel item -> close panel, move to next trigger
					event.preventDefault();
					closeAllDropdowns();
					const nextTriggerIdx = getTriggerIndex(openLi) + 1;
					if (nextTriggerIdx < triggerLis.length) {
						getTriggerElement(triggerLis[nextTriggerIdx]).focus();
					}
					// else let browser move to search/CTA naturally
					return;
				}
			}

			if (shiftKey && currentIdx === 0) {
				// Shift+Tab from first panel item -> close panel, focus trigger
				event.preventDefault();
				closeAllDropdowns();
				getTriggerElement(openLi).focus();
				return;
			}

			// Normal Tab within panel - let browser handle it
			return;
		}

		// Active element is a top-level trigger
		const triggerEl = activeEl.closest('[data-menu-trigger]');
		if (!triggerEl) return;

		const triggerIdx = getTriggerIndex(triggerEl);

		if (!shiftKey && activeEl.getAttribute('aria-expanded') === 'true') {
			// Tab from an open trigger -> enter the panel
			const panelLinks = getDirectPanelLinks(openPanel);
			if (panelLinks.length > 0) {
				event.preventDefault();
				panelLinks[0].focus();
				return;
			}
		}

		// If tabbing away from an open trigger (to next trigger), close it
		if (!shiftKey && triggerIdx === getTriggerIndex(openLi)) {
			closeAllDropdowns();
		}
	}

	/*
	 * Keyboard handler for top-level triggers
	 */

	function handleTriggerKeydown(event) {
		const { key } = event;
		const triggerEl = event.target;
		const li = triggerEl.closest('[data-menu-trigger]');
		if (!li) return;

		const currentIdx = getTriggerIndex(li);
		if (currentIdx === -1) return;

		switch (key) {
			case 'ArrowRight': {
				event.preventDefault();
				const nextIdx = (currentIdx + 1) % triggerLis.length;
				const wasOpen =
					triggerEl.getAttribute('aria-expanded') === 'true';
				if (wasOpen) {
					openDropdown(triggerLis[nextIdx]);
				}
				getTriggerElement(triggerLis[nextIdx]).focus();
				break;
			}

			case 'ArrowLeft': {
				event.preventDefault();
				const prevIdx =
					(currentIdx - 1 + triggerLis.length) % triggerLis.length;
				const wasOpen =
					triggerEl.getAttribute('aria-expanded') === 'true';
				if (wasOpen) {
					openDropdown(triggerLis[prevIdx]);
				}
				getTriggerElement(triggerLis[prevIdx]).focus();
				break;
			}

			case 'ArrowDown': {
				event.preventDefault();
				openDropdown(li);
				const panel = getPanel(li);
				const links = getDirectPanelLinks(panel);
				if (links.length > 0) links[0].focus();
				break;
			}

			case 'ArrowUp': {
				event.preventDefault();
				openDropdown(li);
				const panel = getPanel(li);
				const links = getDirectPanelLinks(panel);
				if (links.length > 0) links[links.length - 1].focus();
				break;
			}

			case 'Enter':
			case ' ': {
				if (
					key === 'Enter' &&
					triggerEl.tagName === 'A' &&
					triggerEl.href
				) {
					return;
				}
				event.preventDefault();
				openDropdown(li);
				const panel = getPanel(li);
				const links = getDirectPanelLinks(panel);
				if (links.length > 0) links[0].focus();
				break;
			}

			case 'Escape': {
				event.preventDefault();
				closeAllDropdowns();
				triggerEl.focus();
				break;
			}

			case 'Home': {
				event.preventDefault();
				getTriggerElement(triggerLis[0]).focus();
				break;
			}

			case 'End': {
				event.preventDefault();
				getTriggerElement(triggerLis[triggerLis.length - 1]).focus();
				break;
			}

			default:
				break;
		}
	}

	/*
	 * Keyboard handler for dropdown panel items
	 */

	function handlePanelKeydown(event) {
		const { key } = event;
		const currentLink = event.target;

		if (currentLink.tagName !== 'A') return;

		const panel = currentLink.closest('[data-menu-target]');
		if (!panel) return;

		if (isInsideFlyout(currentLink)) {
			handleFlyoutKeydown(event);
			return;
		}

		const directLinks = getDirectPanelLinks(panel);
		const currentIdx = directLinks.indexOf(currentLink);

		switch (key) {
			case 'ArrowDown': {
				event.preventDefault();
				const nextIdx =
					currentIdx + 1 < directLinks.length ? currentIdx + 1 : 0;
				directLinks[nextIdx].focus();
				break;
			}

			case 'ArrowUp': {
				event.preventDefault();
				const prevIdx =
					currentIdx - 1 >= 0
						? currentIdx - 1
						: directLinks.length - 1;
				directLinks[prevIdx].focus();
				break;
			}

			case 'ArrowRight': {
				const flyout = getFlyout(currentLink);
				if (flyout) {
					event.preventDefault();
					openFlyout(flyout, currentLink);
					const flyoutItems = getFlyoutLinks(flyout);
					if (flyoutItems.length > 0) flyoutItems[0].focus();
				}
				break;
			}

			case 'ArrowLeft': {
				event.preventDefault();
				const li = panel.closest('[data-menu-trigger]');
				closeAllDropdowns();
				if (li) getTriggerElement(li).focus();
				break;
			}

			case 'Escape': {
				event.preventDefault();
				const li = panel.closest('[data-menu-trigger]');
				closeAllDropdowns();
				if (li) getTriggerElement(li).focus();
				break;
			}

			case 'Home': {
				event.preventDefault();
				if (directLinks.length > 0) directLinks[0].focus();
				break;
			}

			case 'End': {
				event.preventDefault();
				if (directLinks.length > 0) {
					directLinks[directLinks.length - 1].focus();
				}
				break;
			}

			default:
				break;
		}
	}

	/*
	 * Keyboard handler for fly-out submenus (level 4)
	 */

	function handleFlyoutKeydown(event) {
		const { key } = event;
		const currentLink = event.target;
		const flyout = currentLink.closest('[data-flyout]');
		if (!flyout) return;

		const flyoutItems = getFlyoutLinks(flyout);
		const currentIdx = flyoutItems.indexOf(currentLink);

		switch (key) {
			case 'ArrowDown': {
				event.preventDefault();
				const nextIdx =
					currentIdx + 1 < flyoutItems.length ? currentIdx + 1 : 0;
				flyoutItems[nextIdx].focus();
				break;
			}

			case 'ArrowUp': {
				event.preventDefault();
				const prevIdx =
					currentIdx - 1 >= 0
						? currentIdx - 1
						: flyoutItems.length - 1;
				flyoutItems[prevIdx].focus();
				break;
			}

			case 'ArrowLeft':
			case 'Escape': {
				event.preventDefault();
				const parentLi = flyout.closest('li[data-has-children]');
				if (parentLi) {
					const parentLink = parentLi.querySelector(':scope > a');
					closeFlyout(flyout, parentLink);
					if (parentLink) parentLink.focus();
				}
				break;
			}

			default:
				break;
		}
	}

	/*
	 * Focus management - close dropdowns when focus leaves the nav
	 */

	function handleFocusOut() {
		if (closeTimeout) clearTimeout(closeTimeout);
		closeTimeout = setTimeout(() => {
			if (!node.contains(document.activeElement)) {
				closeAllDropdowns();
			}
		}, 150);
	}

	function handleFocusIn() {
		if (closeTimeout) clearTimeout(closeTimeout);
	}

	/*
	 * Init
	 */

	function handleKeydown(event) {
		if (event.key === 'Tab') {
			handleTab(event);
		}
	}

	// Top-level trigger keydown
	triggerLis.forEach(li => {
		const trigger = getTriggerElement(li);
		if (trigger) {
			trigger.addEventListener('keydown', handleTriggerKeydown);
		}
	});

	// Panel keydown
	const allPanels = node.querySelectorAll('[data-menu-target]');
	allPanels.forEach(panel => {
		panel.addEventListener('keydown', handlePanelKeydown);
	});

	// Tab interception on the whole nav
	node.addEventListener('keydown', handleKeydown);
	node.addEventListener('focusout', handleFocusOut);
	node.addEventListener('focusin', handleFocusIn);

	/*
	 * Cleanup
	 */

	return () => {
		triggerLis.forEach(li => {
			const trigger = getTriggerElement(li);
			if (trigger) {
				trigger.removeEventListener('keydown', handleTriggerKeydown);
			}
		});
		allPanels.forEach(panel => {
			panel.removeEventListener('keydown', handlePanelKeydown);
		});
		node.removeEventListener('keydown', handleKeydown);
		node.removeEventListener('focusout', handleFocusOut);
		node.removeEventListener('focusin', handleFocusIn);
		if (closeTimeout) clearTimeout(closeTimeout);
	};
}

export default DesktopMenuA11y;
